import { QueryParams, TAG_TYPES } from 'types';
import { baseApi } from './index';
import { InventoryDocument, InventoryDocument as T } from 'models';
import { getQueryParams } from 'utils';
import { InventoryDocumentResponse } from 'models/InventoryDocument';

const urlEndpoint = `${process.env.REACT_APP_BASE_URL}/inventory-documents`;
const tagType = TAG_TYPES.PurchaseOrder;
const PurchaseOrderApiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllPurchaseOrders: builder.query<
      {
        data: {
          rows: T[];
          count: number;
          kpis: {
            poReceived: number;
            poReady: number;
            poOverdue: number;
            poCancelled: number;
          };
        };
        success: boolean;
      },
      { query: QueryParams }
    >({
      query: (params) => {
        const query = getQueryParams(params?.query);
        return {
          url: `${urlEndpoint}?${query}`,
          method: 'GET',
        };
      },
      providesTags: (result) => {
        return result
          ? [...(result?.data.rows || []).map(({ id }) => ({ type: tagType, id })), { type: tagType, id: 'LIST' }]
          : [{ type: tagType, id: 'LIST' }];
      },
    }),

    getPurchaseOrderById: builder.query<
      {
        data: InventoryDocument;
        success: boolean;
      },
      { id: string; query: QueryParams }
    >({
      query: (params) => {
        const queryString = params.query ? `?${getQueryParams(params.query)}` : '';
        return {
          url: `${urlEndpoint}/${params.id}${queryString}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, params) => [{ type: tagType, id: params.id }],
    }),

    createPurchaseOrder: builder.mutation<InventoryDocumentResponse, Omit<T, 'id'>>({
      query: (data) => ({
        url: urlEndpoint,
        method: 'POST',
        data: {
          ...data,
          type: 'purchase-order',
          isPublishing: false,
        },
      }),
      invalidatesTags: [{ type: tagType, id: 'LIST' }],
    }),

    updatePurchaseOrder: builder.mutation<InventoryDocumentResponse, Partial<T> & { id: string }>({
      query: ({ id, ...patch }) => ({
        url: `${urlEndpoint}/${id}`,
        method: 'PUT',
        data: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: tagType, id },
        { type: tagType, id: 'LIST' },
        { type: TAG_TYPES.Debt, id: 'LIST' },
        { type: TAG_TYPES.Product, id: 'LIST' },
      ],
    }),

    deletePurchaseOrder: builder.mutation<void, string>({
      query: (id) => ({
        url: `${urlEndpoint}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: tagType, id },
        { type: tagType, id: 'LIST' },
      ],
    }),

    cancelPurchaseOrder: builder.mutation<void, string>({
      query: (id) => ({
        url: `${urlEndpoint}/${id}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: tagType, id },
        { type: tagType, id: 'LIST' },
        { type: TAG_TYPES.Debt, id: 'LIST' },
        { type: TAG_TYPES.Product, id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetAllPurchaseOrdersQuery,
  useGetPurchaseOrderByIdQuery,
  useLazyGetPurchaseOrderByIdQuery,
  useCreatePurchaseOrderMutation,
  useUpdatePurchaseOrderMutation,
  useDeletePurchaseOrderMutation,
  useCancelPurchaseOrderMutation,
} = PurchaseOrderApiService;
